import { useMemo, useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import GoogleMapEmbed from '../../../../component/shared/GoogleMapEmbed';
import { useDirectionsOrigin } from '../../../../component/shared/useDirectionsOrigin';

const PAGE_SIZE = 5;

function coordsOf(station) {
  return `${station.lat},${station.lng}`;
}

// Figma station card (493:47518): 0.55px grey border, 10px radius, 15px
// padding, 14px bold name, 12px detail lines; opening hours on one line with
// Opens in green and Closes in red
export function StationHours({ station }) {
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
      <span className="whitespace-nowrap text-[#8e8e8e]">
        Operating Days: <span className="text-[#1fc16b]">{station.operatingDays}</span>
      </span>
      <span className="whitespace-nowrap text-[#1fc16b]">Opens: {station.opens}</span>
      <span className="whitespace-nowrap text-[#d00416]">Closes: {station.closes}</span>
    </p>
  );
}

function StationCard({ station, isSelected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(station)}
      className={`flex w-full flex-col gap-[10px] rounded-[10px] border-[0.55px] p-[15px] text-left transition-colors ${
        isSelected
          ? 'border-[#21990f] bg-[rgba(26,121,12,0.04)]'
          : 'border-[#8e8e8e] bg-white hover:border-[#21990f]'
      }`}
    >
      <h3 className="text-[14px] font-bold text-black">{station.name}</h3>
      <div className="flex flex-col gap-2 text-[12px]">
        <p className="font-medium text-[#777]">{station.type}</p>
        <p className="text-[#8e8e8e]">
          Location: <span className="underline underline-offset-2">{station.location}</span>
        </p>
        <p className="text-[#8e8e8e]">Services Available: {station.services}</p>
        <StationHours station={station} />
      </div>
    </button>
  );
}

function DetailPanel({ station, onClose, onGetDirections, locating, geoError }) {
  return (
    <div className="rounded-[10px] border-[0.55px] border-[#21990f] bg-[rgba(26,121,12,0.04)] p-[15px]">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-[14px] font-bold text-black">{station.name}</h3>
        <button type="button" onClick={onClose} aria-label="Close" className="shrink-0 text-gray-400 hover:text-gray-600">✕</button>
      </div>
      <p className="mt-1 text-xs text-gray-500">{station.type}</p>
      <p className="mt-2 text-xs text-gray-500">
        Location: <span className="text-gray-600 underline underline-offset-2">{station.location}</span>
      </p>
      <p className="mt-1 text-xs text-gray-500">Services Available: {station.services}</p>
      <div className="mt-2">
        <StationHours station={station} />
      </div>
      <p className="mt-2 text-xs text-gray-500">Contact Name: <span className="text-gray-700">{station.contactName}</span></p>
      <p className="mt-1 text-xs text-gray-500">Contact No.: <span className="text-gray-700">{station.contactPhone}</span></p>
      <button
        type="button"
        onClick={onGetDirections}
        disabled={locating}
        className="mt-3 inline-block text-xs font-semibold text-green-700 underline underline-offset-2 hover:text-green-800 disabled:opacity-60"
      >
        {locating ? 'Locating you...' : 'Get Directions'}
      </button>
      {geoError && (
        <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] font-medium leading-snug text-amber-800">
          {geoError}
        </p>
      )}
    </div>
  );
}

function CardPagination({ page, totalPages, total, onGo }) {
  if (totalPages <= 1) return null;
  const start = (page - 1) * PAGE_SIZE + 1;
  const end   = Math.min(page * PAGE_SIZE, total);
  return (
    <div className="flex items-center justify-between gap-2 pt-2">
      <p className="text-[11px] font-medium text-[#8e8e8e]">
        {start}–{end} of {total}
      </p>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onGo(page - 1)}
          disabled={page === 1}
          className="flex h-6 w-6 items-center justify-center rounded border border-[#e8e8e8] bg-white text-[#606060] transition hover:bg-[#f9fafc] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <FiChevronLeft className="h-3 w-3" />
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onGo(n)}
            className={`flex h-6 w-6 items-center justify-center rounded border text-[11px] font-semibold transition ${
              n === page
                ? 'border-[#21990f] bg-[rgba(26,121,12,0.08)] text-[#135809]'
                : 'border-[#e8e8e8] bg-white text-[#606060] hover:bg-[#f9fafc]'
            }`}
          >
            {n}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onGo(page + 1)}
          disabled={page === totalPages}
          className="flex h-6 w-6 items-center justify-center rounded border border-[#e8e8e8] bg-white text-[#606060] transition hover:bg-[#f9fafc] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <FiChevronRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

export default function VeterinaryStations({ stations }) {
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const { origin, locating, error: geoError, requestDirections, reset: resetDirections } = useDirectionsOrigin();

  const totalPages = Math.max(1, Math.ceil(stations.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageItems  = useMemo(
    () => stations.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [stations, safePage]
  );

  const handleSelect = (station) => { setSelected(station); resetDirections(); };
  const handleClose  = () => { setSelected(null); resetDirections(); };
  const goTo = (p) => setPage(Math.max(1, Math.min(totalPages, p)));

  const mapProps = selected
    ? origin
      ? { directions: { origin, destination: coordsOf(selected) } }
      : { place: coordsOf(selected) }
    : { place: 'Abuja, Nigeria' };

  return (
    // Figma: 380px card column beside an 860×596 map, 16px apart
    <div className={`grid gap-4 ${selected ? 'grid-cols-1 md:grid-cols-[300px_1fr] pub-xl:grid-cols-[380px_300px_1fr]' : 'grid-cols-1 md:grid-cols-[300px_1fr] pub-xl:grid-cols-[380px_1fr]'}`}>

      {/* Card list with pagination */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3">
          {pageItems.length === 0 ? (
            <p className="rounded-[10px] border-[0.55px] border-[#8e8e8e] bg-white p-[15px] text-center text-[12px] text-[#8e8e8e]">
              No veterinary stations match your filters.
            </p>
          ) : (
            pageItems.map((station) => (
              <StationCard
                key={station.id}
                station={station}
                isSelected={selected?.id === station.id}
                onSelect={handleSelect}
              />
            ))
          )}
        </div>
        <CardPagination page={safePage} totalPages={totalPages} total={stations.length} onGo={goTo} />
      </div>

      {selected && (
        <DetailPanel
          station={selected}
          onClose={handleClose}
          onGetDirections={requestDirections}
          locating={locating}
          geoError={geoError}
        />
      )}

      <div className="h-[260px] overflow-hidden rounded-[10px] border-[0.55px] border-[#8e8e8e] sm:h-[400px] lg:h-[596px]">
        <GoogleMapEmbed
          title={selected ? selected.name : 'Veterinary stations map'}
          {...mapProps}
        />
      </div>
    </div>
  );
}
